import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import ReciboTable from '@/components/recibos/ReciboTable';
import ReciboTypeTabs from '@/components/recibos/ReciboTypeTabs';
import reciboService from '@/services/reciboService';
import { TipoPagoRecibo } from '@/types/recibo';
import {
  addLocalDays,
  formatReceiptDayHeading,
  parseLocalDateKey,
  toLocalDateKey,
} from '@/utils/localDateUtils';
import {
  emptyReciboListResult,
  normalizeReciboListResult,
  parseReciboReportTab,
  reciboReportTabToType,
  type ReciboReportTab,
  type ReciboListResult,
} from '@/utils/reciboListUtils';

const PAGE_SIZE = 25;

export default function RecentRecibos() {
  const [searchParams, setSearchParams] = useSearchParams();
  const todayKey = toLocalDateKey(new Date());
  const requestedDate = searchParams.get('date');
  const requestedTab = searchParams.get('tab');
  const activeTab = parseReciboReportTab(requestedTab);
  const reportType = reciboReportTabToType(activeTab);
  const selectedDate = parseLocalDateKey(requestedDate) ?? parseLocalDateKey(todayKey)!;
  const selectedDateKey = toLocalDateKey(selectedDate);
  const [result, setResult] = useState<ReciboListResult>(emptyReciboListResult);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadReceipts = useCallback(async (nextPage: number, dateKey: string, type: TipoPagoRecibo) => {
    setLoading(true);
    setError(false);
    try {
      const response = await reciboService.searchRecibos({
        fecha: dateKey,
        tipo_pago: type,
        estado: 'ACTIVO',
        page: nextPage,
        limit: PAGE_SIZE,
      });
      const nextResult = normalizeReciboListResult(response, nextPage, PAGE_SIZE);
      setResult(nextResult);
      setPage(nextResult.page);
    } catch (loadError) {
      console.error('Error fetching recent receipts:', loadError);
      setResult(emptyReciboListResult);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (requestedDate !== selectedDateKey || requestedTab !== activeTab) {
      setSearchParams({ date: selectedDateKey, tab: activeTab }, { replace: true });
      return;
    }
    setPage(1);
    void loadReceipts(1, selectedDateKey, reportType);
  }, [activeTab, loadReceipts, reportType, requestedDate, requestedTab, selectedDateKey, setSearchParams]);

  const moveDay = (amount: number) => {
    const nextDateKey = toLocalDateKey(addLocalDays(selectedDate, amount));
    if (nextDateKey > todayKey) return;
    setSearchParams({ date: nextDateKey, tab: activeTab });
  };

  const changeTab = (tab: ReciboReportTab) => {
    if (tab === activeTab) return;
    setSearchParams({ date: selectedDateKey, tab });
  };

  return (
    <MainLayout>
      <div className="mx-auto max-w-[1680px] p-4 sm:p-6">
        <div className="mb-6">
          <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
            <Link to="/receipts" className="hover:text-gray-950">Recibos</Link>
            <span aria-hidden="true">/</span>
            <span className="text-gray-950">Recibos Recientes</span>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-950">Recibos Recientes</h1>
              <p className="mt-1 text-gray-600">Recibos creados durante el día seleccionado</p>
            </div>
            <Link to="/receipts" className="text-sm font-semibold text-primary hover:underline">
              Ver todos los recibos
            </Link>
          </div>
        </div>

        <div className="mb-5 grid grid-cols-[2.75rem_minmax(0,1fr)_2.75rem] items-center rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white px-3 py-3 shadow-sm sm:px-5">
          <button
            type="button"
            aria-label="Ver recibos del día anterior"
            onClick={() => moveDay(-1)}
            className="flex h-11 w-11 items-center justify-center justify-self-start rounded-xl border border-slate-200 bg-white text-primary shadow-sm transition hover:-translate-x-0.5 hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <div className="min-w-0 px-3 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Fecha de emisión</p>
            <h2 className="mt-1 text-base font-bold text-primary sm:text-xl">
              {formatReceiptDayHeading(selectedDate)}
            </h2>
          </div>
          <button
            type="button"
            aria-label="Ver recibos del día siguiente"
            onClick={() => moveDay(1)}
            disabled={selectedDateKey >= todayKey}
            className="flex h-11 w-11 items-center justify-center justify-self-end rounded-xl border border-slate-200 bg-white text-primary shadow-sm transition hover:translate-x-0.5 hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:translate-x-0"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </div>

        <ReciboTypeTabs activeTab={activeTab} onChange={changeTab} />

        <div
          id="receipt-report-panel"
          role="tabpanel"
          aria-labelledby={`receipt-tab-${activeTab}`}
        >
          <ReciboTable
            receipts={result.items}
            loading={loading}
            reportType={reportType}
            onReceiptUpdated={() => void loadReceipts(page, selectedDateKey, reportType)}
            emptyState={
              <div className="py-14 text-center">
                <div className="mb-4 text-5xl">🧾</div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900">
                  {error ? 'No se pudieron cargar los recibos' : `No se crearon recibos de ${activeTab} este día`}
                </h3>
                <p className="text-gray-600">
                  {error ? 'Intente nuevamente en unos momentos.' : 'Use las flechas para consultar otra fecha.'}
                </p>
              </div>
            }
          />
        </div>

        {result.items.length > 0 ? (
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-600">
              Mostrando {((page - 1) * PAGE_SIZE) + 1} a {Math.min(page * PAGE_SIZE, result.total)} de {result.total} recibos
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => void loadReceipts(page - 1, selectedDateKey, reportType)}
                className="rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="px-2 text-sm text-gray-700">Página {page} de {result.totalPages}</span>
              <button
                type="button"
                disabled={page >= result.totalPages}
                onClick={() => void loadReceipts(page + 1, selectedDateKey, reportType)}
                className="rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </MainLayout>
  );
}
